<template>
  <div class="reports-header">
    <template v-if="!isMobile">
      <div class="top-section">

        <!-- Open filter dialog button -->
          <v-btn
            class="mr-2"
            :yellow="hasFilters"
            medium
            @click="modal.showFilters = true">
            <v-icon
              position-left
              class="mr-1"
              font-size="1"
              icon="filter"/>
            {{ 'reports.add_filters' | translate }}
          </v-btn>

        <!-- Create report button -->
          <v-dropdown
            auto-close
            width-auto
            position-right>
            <v-btn
              medium
              :disabled="!totalCount"
              :loading="loading">
              <v-icon
                position-left
                font-size="1"
                icon="file-download"/>
              <span> {{ 'reports.create_report' | translate }} </span>
            </v-btn>
            <template
              v-if="!!totalCount"
              slot="content">
              <v-card
                elevated
                overflow-hidden
                style="width: 340px">
                <v-scrollable
                  :bottom-offset="50">
                  <v-list>
                    <v-list-item 
                      v-for="report in reports"
                      :key="report.id"
                      @click.native="getReport(report)">
                      {{ report.name | translate }}
                    </v-list-item>
                  </v-list>
                </v-scrollable>
              </v-card>
            </template>
          </v-dropdown>
      </div>
    </template>
    <template v-else>
      <v-btn
        fab
        :yellow="hasFilters"
        @click="modal.showFilters = true">
        <v-icon
          font-size="1.2"
          icon="filter"/>
      </v-btn>
    </template>
    <!-- Tabs -->
    <report-tabs/>

    <v-dialog
      max-width="700"
      v-model="modal.showFilters">
      <filters-modal 
        @close="modal.showFilters = false"/>
    </v-dialog>

    <v-dialog
      max-width="400"
      v-model="modal.showCreateBlank">
      <create-blank-modal
        :link="link"
        :report="report"
        @close="modal.showCreateBlank = false"/>
    </v-dialog>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'

import adaptationMixin from '@/mixins/adaptation.mixin.js'

import FileSaver from 'file-saver'
import FiltersModal from './modals/FiltersModal'
import CreateBlankModal from './modals/CreateBlankModal'
import ReportTabs from './common/ReportTabs'

export default {
  name: 'ReportsHeader',

  mixins: [adaptationMixin],

  components: {
    FiltersModal,
    CreateBlankModal,
    ReportTabs
  },

  data: () => ({
    showFiltersDialog: false,
    modal: {
      showFilters: false,
      showCreateBlank: false
    },
    loading: false,
    reports: [],
    labels: {
      dashboard: 'reports.types.dashboard',
      planfeed: 'reports.types.planfeed',
      plan_animalfeed: 'reports.types.plan_animalfeed',
      schedule_load: 'reports.types.schedule_load',
      load_ings_by_groups: 'reports.types.load_ings_by_groups',
      load_ings_by_groups_with_leftovers: 'reports.types.load_ings_by_groups_with_leftovers',
      upload_groups: 'reports.types.upload_groups',
      upload_ingredients: 'reports.types.upload_ingredients',
      schedule_upload: 'reports.types.schedule_upload',
      inout: 'reports.types.inout',
      inoutnew: 'reports.types.inout_new',
      form8: 'reports.types.form8',
      form9: 'reports.types.form9',
      schedule_loading_time: 'reports.types.schedule_loading_time',
      mhp_schedule_1c: 'reports.types.mhp_schedule_1c',
      mhp_report_1c: 'reports.types.mhp_report_1c',
      mhp_ration_1c: 'reports.types.mhp_ration_1c',
      vitagro_report_1c: 'reports.types.vitagro_report_1c',
      animalfeed_schedule_load: 'reports.types.animalfeed_schedule_load',
      load_upload: 'reports.types.load_upload',
      ration_consumption: 'reports.types.ration_consumption',
      ration_archive: 'reports.types.ration_archive',
    },
    reportsToShow: [
      'schedule_loading_time',
      'load_ings_by_groups',
      'plan_animalfeed',
      'planfeed',
      'schedule_load',
      'schedule_upload',
      'inout',
      'ration_consumption',
      'load_upload',
      'ration_archive',
    ],
    link: '',
    report: ''
  }),

  computed: {
    ...mapGetters('reports', [
      'reportsList',
      'filters',
      'hasFilters',
      'totalCount'
    ]),
    lang () {
      return this.$store.getters['account/lang']
    }
  },

  mounted () {
    this.loadReportsList()
  },

  methods: {
    ...mapActions('reports', [
      'getReportList',
      'makeReport',
      'applyFilters'
    ]),

    async loadReportsList () {
      const items = []
      const lang = this.lang
      await this.getReportList()
      for (const section in this.reportsList) {
        if (section !== 'count') {
          for (const type in this.reportsList[section]) {
            if (lang === 'uk' || this.reportsToShow.includes(type)) {
              const item = {
                id: items.length,
                name: this.labels[type] ? this.labels[type] : type,
                link: section,
                filter: type,
                need_blank: this.reportsList[section][type]
              }
              items.push(item)
            }
          }
        }
      }
      this.reports = items
    },

    async getReport (report) {
      if (report) {
        this.filters.report = report.filter
        if (report.need_blank) {
          this.link = report.link
          this.report = report.filter
          this.modal.showCreateBlank = true
        } else {
          this.loading = true
          if (report.link === 'animalfeed_dashboard') {
            report.link = 'animalfeed/dashboard'
          }

          this.downloadReport(await this.makeReport(report.link, this.filters))
          this.loading = false
        }
      } else {
        this.$toasted.info(this.$t('info.select_report_type'))
      }
    },

    b64toFile (b64Data, filename, contentType) {
      const sliceSize = 512
      const byteCharacters = atob(b64Data)
      const byteArrays = []

      for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize)
        const byteNumbers = new Array(slice.length)

        for (let i = 0; i < slice.length; i++) {
          byteNumbers[i] = slice.charCodeAt(i)
        }
        const byteArray = new Uint8Array(byteNumbers)
        byteArrays.push(byteArray)
      }
      return new File(byteArrays, filename, {type: contentType})
    },

    async downloadReport ({ report, name }) {
      const file = this.b64toFile(report, name, 'application/vnd.ms-excel;charset=utf-8')
      FileSaver.saveAs(file, name)
    },

    openFiltersDialog () {
      this.showFiltersDialog = true
    },

    closeFiltersDialog () {
      this.showFiltersDialog = false
    },

    openCreateReportDialog () {
      this.showCreateReportDialog = true
    },

    closeCreateReportsDialog () {
      this.showCreateReportDialog = false
    }
  },
  watch: {
    lang: {
      immediate: true,
      handler (newLang, oldLang) {
        this.loadReportsList()
      }
    }
  }
}
</script>

<style scoped>
  .reports-header {
    margin-bottom: 2rem;
  }

  .top-section {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 25px;
  }
</style>
